import { Loader } from 'components/ai-elements/loader';
import { MessageResponse } from 'components/ai-elements/message';
import React, { useEffect, useState } from 'react';
import { captureArtifactError } from 'sentry/artifact';

// Don't pull very large text files into the DOM — the artifact header's
// Download button is the escape hatch.
const MAX_TEXT_PREVIEW_BYTES = 2 * 1024 * 1024;

const ErrorNotice = ({ message }: { message: string }) => (
  <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
    {message}
  </div>
);

const CenteredLoader = () => (
  <div className="flex items-center justify-center py-8">
    <Loader />
  </div>
);

type ViewerProps = { url: string };

/**
 * Renders a PDF artifact in the browser's built-in PDF viewer.
 *
 * The file is fetched and shown from a blob: URL rather than pointing the
 * iframe at the presigned S3 URL: the app CSP's frame-src does not allow the
 * chatbot S3 bucket (only connect-src does) but does allow blob:, and a
 * blocked iframe src fails silently with a blank pane.
 */
export const PdfViewer = ({ url }: ViewerProps) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let created: string | null = null;

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load PDF: ${res.status}`);
        return res.arrayBuffer();
      })
      .then((buffer) => {
        if (cancelled) return;
        // Force the type: the built-in viewer only engages for application/pdf,
        // and older copies in S3 may carry a generic content type.
        created = URL.createObjectURL(new Blob([buffer], { type: 'application/pdf' }));
        setObjectUrl(created);
      })
      .catch((err) => {
        if (cancelled) return;
        captureArtifactError(err, { url, kind: 'pdf' });
        setError(err.message);
      });

    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [url]);

  if (error) return <ErrorNotice message={error} />;
  if (objectUrl === null) return <CenteredLoader />;

  return <iframe src={objectUrl} title="PDF preview" className="size-full min-h-[600px] border-0" />;
};

type TextDocumentViewerProps = ViewerProps & {
  /** Render as Markdown; otherwise shown verbatim. */
  markdown: boolean;
};

/** Renders a Markdown or plain-text artifact. */
export const TextDocumentViewer = ({ url, markdown }: TextDocumentViewerProps) => {
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load file: ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (blob.size > MAX_TEXT_PREVIEW_BYTES) {
          throw new Error('File is too large to preview — use Download to open it.');
        }
        return blob.text();
      })
      .then((content) => {
        if (!cancelled) setText(content);
      })
      .catch((err) => {
        if (cancelled) return;
        captureArtifactError(err, { url, kind: 'text' });
        setError(err.message);
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  if (error) return <ErrorNotice message={error} />;
  if (text === null) return <CenteredLoader />;

  if (markdown) {
    return <MessageResponse className="text-sm">{text}</MessageResponse>;
  }
  return <pre className="whitespace-pre-wrap break-words font-mono text-xs text-foreground">{text}</pre>;
};
